import { useState } from "react";
import { Phone, Mail, MapPin, Facebook, Instagram, Linkedin, Send } from "lucide-react";
import { toast } from "sonner";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { GraffitiTag } from "@/components/GraffitiTag";
import { useContent } from "@/context/ContentContext";
import { api, formatApiError } from "@/lib/api";

const inputCls =
  "w-full bg-surface border border-white/10 focus:border-gold px-4 py-3 text-sm text-white placeholder:text-zinc-500 outline-none transition-colors duration-300";

export default function Contact() {
  const { content } = useContent();
  const socials = content.socials || {};
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", fleet_size: "", message: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please complete your name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent. Our team will reach out shortly.");
      setForm({ name: "", email: "", phone: "", fleet_size: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToForm = () => document.getElementById("contact-form")?.scrollIntoView({ behavior: "smooth" });

  return (
    <main data-testid="contact-page" className="pt-32 pb-24 px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto">
      <Seo
        title="Contact | B&H Truck Dispatching"
        description="Contact B&H Truck Dispatching to discuss dispatching services for your trucking operation. Independent owner-operators and growing fleets welcome."
      />
      <Reveal>
        <p className="overline-label mb-4">Contact</p>
        <div className="relative inline-block">
          <GraffitiTag testId="contact-graffiti-tag" className="absolute -top-8 sm:-top-10 -right-1 sm:-right-8 z-10 text-xl sm:text-3xl" rotate={-5}>
            Roll With Us.
          </GraffitiTag>
          <h1 className="font-display text-4xl sm:text-6xl font-extrabold uppercase tracking-tight text-white leading-[1.02] max-w-3xl">
            Let's Move Your Business <span className="gold-text">Forward.</span>
          </h1>
        </div>
        <p className="mt-5 text-sm sm:text-base text-zinc-400 max-w-xl leading-relaxed">
          Whether you're an independent owner-operator or managing a growing fleet, B&amp;H Truck Dispatching is ready to
          discuss how our dispatching services can support your operation.
        </p>
        <GraffitiTag testId="contact-slogan-tag" className="block w-fit mt-7 mb-2 text-2xl sm:text-4xl" color="text-champagne" rotate={-3} delay={0.5}>
          Solid Loads. Sound Advice.
        </GraffitiTag>
        <button data-testid="request-consultation-btn" onClick={scrollToForm} className="btn-gold mt-8">
          Request a Consultation
        </button>
      </Reveal>

      <div className="grid lg:grid-cols-5 gap-10 lg:gap-16 mt-16 lg:mt-24">
        <Reveal className="lg:col-span-2 space-y-4">
          {[
            { icon: Phone, label: "Phone", value: content.phone, href: `tel:${content.phone}`, testId: "contact-phone" },
            { icon: Mail, label: "Email", value: content.email, href: `mailto:${content.email}`, testId: "contact-email" },
            { icon: MapPin, label: "Location / Service Area", value: content.location, testId: "contact-location" },
          ].map((c) => (
            <div key={c.label} data-testid={c.testId} className="card-gold p-6 flex items-center gap-5">
              <div className="w-11 h-11 shrink-0 flex items-center justify-center border border-gold/40 bg-gold/5">
                <c.icon size={18} className="text-gold" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">{c.label}</p>
                {c.href ? (
                  <a href={c.href} className="text-white text-sm sm:text-base hover:text-champagne transition-colors duration-300">{c.value}</a>
                ) : (
                  <p className="text-white text-sm sm:text-base">{c.value}</p>
                )}
              </div>
            </div>
          ))}
          {(socials.facebook || socials.instagram || socials.linkedin) && (
            <div className="flex gap-3 pt-2">
              {socials.facebook && (
                <a href={socials.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="contact-social-facebook" className="p-3 border border-gold/30 text-gold hover:bg-gold hover:text-ink transition-colors duration-300"><Facebook size={18} /></a>
              )}
              {socials.instagram && (
                <a href={socials.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="contact-social-instagram" className="p-3 border border-gold/30 text-gold hover:bg-gold hover:text-ink transition-colors duration-300"><Instagram size={18} /></a>
              )}
              {socials.linkedin && (
                <a href={socials.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" data-testid="contact-social-linkedin" className="p-3 border border-gold/30 text-gold hover:bg-gold hover:text-ink transition-colors duration-300"><Linkedin size={18} /></a>
              )}
            </div>
          )}
        </Reveal>

        <Reveal delay={0.12} className="lg:col-span-3">
          <form id="contact-form" data-testid="contact-form" onSubmit={submit} className="card-gold p-6 sm:p-10 grid sm:grid-cols-2 gap-5">
            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-400 block mb-2">Name *</label>
              <input data-testid="contact-name-input" className={inputCls} value={form.name} onChange={set("name")} placeholder="Your full name" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-400 block mb-2">Email *</label>
              <input data-testid="contact-email-input" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@company.com" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-400 block mb-2">Phone</label>
              <input data-testid="contact-phone-input" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="(555) 000-0000" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-400 block mb-2">Fleet Size</label>
              <select data-testid="contact-fleet-select" className={inputCls} value={form.fleet_size} onChange={set("fleet_size")}>
                <option value="">Select fleet size</option>
                <option value="1 truck">1 truck</option>
                <option value="2-5 trucks">2–5 trucks</option>
                <option value="6-15 trucks">6–15 trucks</option>
                <option value="16+ trucks">16+ trucks</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs uppercase tracking-wider text-zinc-400 block mb-2">Message *</label>
              <textarea data-testid="contact-message-input" rows={5} className={inputCls} value={form.message} onChange={set("message")} placeholder="Tell us about your operation and how we can help" />
            </div>
            <div className="sm:col-span-2">
              <button type="submit" data-testid="contact-submit-btn" disabled={submitting} className="btn-gold w-full sm:w-auto disabled:opacity-50 disabled:pointer-events-none">
                <Send size={15} /> {submitting ? "Sending…" : "Send Message"}
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </main>
  );
}
